// schema.js - Utilities for generating structured data (JSON-LD)
// Created by Claude on 20.05.2025 for SEO optimization

/**
 * Generate Organization schema for the drift team
 */
export const getOrganizationSchema = (language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsTeam',
    'name': 'NO RISK NO DRIFT',
    'url': url,
    'logo': `${url}/images/logo.png`,
    'sameAs': [
      'https://instagram.com/norisknodrift',
      'https://facebook.com/norisknodrift',
      'https://youtube.com/norisknodrift',
      'https://tiktok.com/norisknodrift'
    ],
    'description': language === 'cs' 
      ? 'NO RISK NO DRIFT je profesionální drift team z České Lípy. Závodíme, předvádíme a nabízíme zážitky s driftem.'
      : 'NO RISK NO DRIFT is a professional drift team from Česká Lípa. We race, perform and offer drift experiences.',
    'foundingDate': '2018',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Česká Lípa',
      'addressRegion': 'Liberecký kraj',
      'addressCountry': 'CZ'
    }
  };
};

/**
 * Generate schema for a product
 */
export const getProductSchema = (product, language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name,
    'description': product.description,
    'image': product.image_url,
    'url': `${url}/product/${product.slug}`,
    'sku': product.id.toString(),
    'brand': {
      '@type': 'Brand',
      'name': 'NO RISK NO DRIFT'
    },
    'offers': {
      '@type': 'Offer',
      'price': product.price.toString(),
      'priceCurrency': 'CZK',
      'availability': product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      'url': `${url}/product/${product.slug}`
    }
  };
};

/**
 * Generate schema for a blog post
 */
export const getBlogPostSchema = (post, language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': post.title,
    'description': post.excerpt || post.title,
    'image': post.image_url,
    'datePublished': post.created_at,
    'dateModified': post.updated_at || post.created_at,
    'author': {
      '@type': 'Person',
      'name': post.author || 'NO RISK NO DRIFT Team'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'NO RISK NO DRIFT',
      'logo': {
        '@type': 'ImageObject',
        'url': `${url}/images/logo.png`
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${url}/blog/${post.slug}`
    }
  };
};

/**
 * Generate schema for an event
 */
export const getEventSchema = (event, language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    'name': event.title,
    'description': event.description,
    'image': event.image_url,
    'startDate': event.start_date,
    'endDate': event.end_date || event.start_date,
    'location': {
      '@type': 'Place',
      'name': event.location,
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': event.city,
        'addressCountry': event.country || 'CZ'
      }
    },
    'organizer': {
      '@type': 'Organization',
      'name': 'NO RISK NO DRIFT',
      'url': url
    },
    'eventStatus': 'https://schema.org/EventScheduled',
    'url': `${url}/events-calendar`
  };
};

/**
 * Generate schema for a car
 */
export const getCarSchema = (car, language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    'name': car.name,
    'description': car.description,
    'image': car.main_image_url,
    'brand': {
      '@type': 'Brand',
      'name': car.make
    },
    'model': car.model,
    'vehicleEngine': {
      '@type': 'EngineSpecification',
      'enginePower': {
        '@type': 'QuantitativeValue',
        'value': car.power,
        'unitCode': 'KWT'
      }
    },
    'url': `${url}/car/${car.slug}`
  };
};

/**
 * Generate breadcrumbs schema
 */
export const getBreadcrumbsSchema = (items, language = 'cs') => {
  const url = language === 'cs' ? 'https://norisknodrift.cz' : 'https://norisknodrift.com';
  
  const itemListElement = items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': `${url}${item.path}`
  }));
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': itemListElement
  };
};

export default {
  getOrganizationSchema,
  getProductSchema,
  getBlogPostSchema,
  getEventSchema,
  getCarSchema,
  getBreadcrumbsSchema
};